import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';

import Button from '../../components/ui/Button';
import SearchAndFilters from './components/SearchAndFilters';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import MemberTable from './components/MemberTable';
import MemberMobileCard from './components/MemberMobileCard';
import PaginationControls from './components/PaginationControls';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const MemberList = () => {
  const navigate = useNavigate();
  
  // State management
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [isMobile, setIsMobile] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, member: null, isMultiple: false });
  
  // Filter state
  const [filters, setFilters] = useState({
    region: '',
    status: '',
    dateRange: '',
    startDate: '',
    endDate: ''
  });

  // Mock member data
  const mockMembers = [
    {
      id: 1,
      name: "João Silva",
      email: "joao.silva@email.com",
      cpf: "123.456.789-01",
      voterId: "VT001234567",
      region: "Nordeste",
      phone: "(11) 91234-5678",
      registrationDate: "2024-01-15",
      status: "Ativo"
    },
    {
      id: 2,
      name: "Ana Souza",
      email: "ana.souza@email.com",
      cpf: "234.567.890-12",
      voterId: "VT002345678",
      region: "Sul",
      phone: "(21) 99876-5432",
      registrationDate: "2024-02-20",
      status: "Ativo"
    },
    {
      id: 3,
      name: "Carlos Pereira",
      email: "carlos.pereira@email.com",
      cpf: "345.678.901-23",
      voterId: "VT003456789",
      region: "Sudeste",
      phone: "(31) 98765-4321",
      registrationDate: "2024-01-10",
      status: "Pendente"
    },
    {
      id: 4,
      name: "Mariana Oliveira",
      email: "mariana.oliveira@email.com",
      cpf: "456.789.012-34",
      voterId: "VT004567890",
      region: "Centro-Oeste",
      phone: "(41) 97654-3210",
      registrationDate: "2024-03-05",
      status: "Inativo"
    },
    {
      id: 5,
      name: "Ricardo Almeida",
      email: "ricardo.almeida@email.com",
      cpf: "567.890.123-45",
      voterId: "VT005678901",
      region: "Norte",
      phone: "(51) 96543-2109",
      registrationDate: "2024-02-28",
      status: "Ativo"
    },
    {
      id: 6,
      name: "Fernanda Costa",
      email: "fernanda.costa@email.com",
      cpf: "678.901.234-56",
      voterId: "VT006789012",
      region: "Nordeste",
      phone: "(61) 95432-1098",
      registrationDate: "2024-01-25",
      status: "Suspenso"
    },
    {
      id: 7,
      name: "Paulo Santos",
      email: "paulo.santos@email.com",
      cpf: "789.012.345-67",
      voterId: "VT007890123",
      region: "Sul",
      phone: "(71) 94321-0987",
      registrationDate: "2024-03-12",
      status: "Ativo"
    },
    {
      id: 8,
      name: "Beatriz Lima",
      email: "beatriz.lima@email.com",
      cpf: "890.123.456-78",
      voterId: "VT008901234",
      region: "Sudeste",
      phone: "(81) 93210-9876",
      registrationDate: "2024-02-14",
      status: "Ativo"
    },
    {
      id: 9,
      name: "Lucas Fernandes",
      email: "lucas.fernandes@email.com",
      cpf: "901.234.567-89",
      voterId: "VT009012345",
      region: "Centro-Oeste",
      phone: "(91) 92109-8765",
      registrationDate: "2024-01-30",
      status: "Pendente"
    },
    {
      id: 10,
      name: "Gabriela Rocha",
      email: "gabriela.rocha@email.com",
      cpf: "012.345.678-90",
      voterId: "VT010123456",
      region: "Norte",
      phone: "(85) 91098-7654",
      registrationDate: "2024-03-08",
      status: "Ativo"
    }
  ];
  

  // Initialize data
  useEffect(() => {
    const loadMembers = async () => {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMembers(mockMembers);
      setLoading(false);
    };

    loadMembers();
  }, []);

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter and search logic
  const filteredMembers = useMemo(() => {
    let filtered = members?.filter(member => {
      // Search filter
      const searchLower = searchTerm?.toLowerCase();
      const matchesSearch = !searchTerm || 
        member?.name?.toLowerCase()?.includes(searchLower) ||
        member?.cpf?.includes(searchTerm) ||
        member?.voterId?.toLowerCase()?.includes(searchLower) ||
        member?.email?.toLowerCase()?.includes(searchLower);

      // Region filter
      const matchesRegion = !filters?.region || member?.region === filters?.region;

      // Status filter
      const matchesStatus = !filters?.status || member?.status === filters?.status;

      // Date range filter
      let matchesDate = true;
      if (filters?.dateRange) {
        const memberDate = new Date(member.registrationDate);
        const now = new Date();
        
        switch (filters?.dateRange) {
          case 'today':
            matchesDate = memberDate?.toDateString() === now?.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = memberDate >= weekAgo;
            break;
          case 'month':
            matchesDate = memberDate?.getMonth() === now?.getMonth() && 
                         memberDate?.getFullYear() === now?.getFullYear();
            break;
          case 'quarter':
            const quarter = Math.floor(now?.getMonth() / 3);
            const memberQuarter = Math.floor(memberDate?.getMonth() / 3);
            matchesDate = memberQuarter === quarter && 
                         memberDate?.getFullYear() === now?.getFullYear();
            break;
          case 'year':
            matchesDate = memberDate?.getFullYear() === now?.getFullYear();
            break;
          case 'custom':
            if (filters?.startDate && filters?.endDate) {
              const startDate = new Date(filters.startDate);
              const endDate = new Date(filters.endDate);
              matchesDate = memberDate >= startDate && memberDate <= endDate;
            }
            break;
        }
      }

      return matchesSearch && matchesRegion && matchesStatus && matchesDate;
    });

    // Sort filtered results
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'registrationDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [members, searchTerm, filters, sortConfig]);

  // Pagination logic
  const totalPages = Math.ceil(filteredMembers?.length / itemsPerPage);
  const paginatedMembers = filteredMembers?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Event handlers
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      region: '',
      status: '',
      dateRange: '',
      startDate: '',
      endDate: ''
    });
    setCurrentPage(1);
  };

  const handleSort = (config) => {
    setSortConfig(config);
  };

  const handleSelectMember = (memberId, isSelected) => {
    if (isSelected) {
      setSelectedMembers(prev => [...prev, memberId]);
    } else {
      setSelectedMembers(prev => prev?.filter(id => id !== memberId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedMembers(paginatedMembers?.map(member => member?.id));
    } else {
      setSelectedMembers([]);
    }
  };

  const handleDeselectAll = () => {
    setSelectedMembers([]);
  };

  const handleEditMember = (member) => {
    navigate('/member-registration', { state: { editMember: member } });
  };

  const handleDeleteMember = (member) => {
    setDeleteModal({
      isOpen: true,
      member,
      isMultiple: false
    });
  };

  const handleViewMemberDetails = (member) => {
    navigate('/member-details', { state: { memberId: member?.id } });
  };

  const handleBulkDelete = () => {
    setDeleteModal({
      isOpen: true,
      member: null,
      isMultiple: true
    });
  };

  const handleBulkStatusUpdate = (status) => {
    // Simulate bulk status update
    setMembers(prev => prev?.map(member => 
      selectedMembers?.includes(member?.id) 
        ? { ...member, status }
        : member
    ));
    setSelectedMembers([]);
  };

  const handleBulkExport = () => {
    // Simulate export functionality
    const selectedMemberData = members?.filter(member => 
      selectedMembers?.includes(member?.id)
    );
    
    const csvContent = [
      ['Name', 'Email', 'CPF', 'Voter ID', 'Region', 'Phone', 'Registration Date', 'Status'],
      ...selectedMemberData?.map(member => [
        member?.name,
        member?.email,
        member?.cpf,
        member?.voterId,
        member?.region,
        member?.phone,
        member?.registrationDate,
        member?.status
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `members-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const confirmDelete = () => {
    if (deleteModal?.isMultiple) {
      setMembers(prev => prev?.filter(member => !selectedMembers?.includes(member?.id)));
      setSelectedMembers([]);
    } else {
      setMembers(prev => prev?.filter(member => member?.id !== deleteModal?.member?.id));
    }
    setDeleteModal({ isOpen: false, member: null, isMultiple: false });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Cabeçalho da página */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Lista de Membros</h1>
              <p className="text-muted-foreground">
                Gerencie e navegue por todos os filiados com filtros avançados e busca
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate('/member-import')}
                iconName="Upload"
                iconPosition="left"
              >
                Importar membros
              </Button>
              <Button
                onClick={() => navigate('/member-registration')}
                iconName="UserPlus"
                iconPosition="left"
              >
                Adicionar membro
              </Button>
            </div>
          </div>

          {/* Search and Filters */}
          <SearchAndFilters
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            totalResults={filteredMembers?.length}
          />

          {/* Bulk Actions Toolbar */}
          <BulkActionsToolbar
            selectedCount={selectedMembers?.length}
            onBulkDelete={handleBulkDelete}
            onBulkStatusUpdate={handleBulkStatusUpdate}
            onBulkExport={handleBulkExport}
            onDeselectAll={handleDeselectAll}
          />

          {/* Member List */}
          {isMobile ? (
            <div className="space-y-4">
              {paginatedMembers?.map((member) => (
                <MemberMobileCard
                  key={member?.id}
                  member={member}
                  isSelected={selectedMembers?.includes(member?.id)}
                  onSelect={handleSelectMember}
                  onEdit={handleEditMember}
                  onDelete={handleDeleteMember}
                  onViewDetails={handleViewMemberDetails}
                />
              ))}
            </div>
          ) : (
            <MemberTable
              members={paginatedMembers}
              sortConfig={sortConfig}
              onSort={handleSort}
              selectedMembers={selectedMembers}
              onSelectMember={handleSelectMember}
              onSelectAll={handleSelectAll}
              onEditMember={handleEditMember}
              onDeleteMember={handleDeleteMember}
              onViewMemberDetails={handleViewMemberDetails}
            />
          )}

          {/* Pagination */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredMembers?.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </main>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal?.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, member: null, isMultiple: false })}
        onConfirm={confirmDelete}
        memberName={deleteModal?.member?.name}
        isMultiple={deleteModal?.isMultiple}
        count={selectedMembers?.length}
      />
      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loading}
        message="Carregando dados dos membros..."
      />
    </div>
  );
};

export default MemberList;